'use client';

/**
 * HomeHeroCard — page-level greeting + subtitle.
 *
 * Reuses `pipeline_summary`'s labels (`hero_title_*`, `hero_subtitle`)
 * — already fetched eagerly for the pipeline card, so no extra BFF
 * call.
 *
 * The greeting is time-aware: the server ships morning/afternoon/evening
 * phrasings (`hero_title_morning|afternoon|evening`) and this component
 * picks one off the viewer's local clock — the server can't, it doesn't
 * know the viewer's timezone.
 *
 * @see klyde_edge/composition/cards/home.py — content_key="home_hero"
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Home
 * @layer: presentation
 */

import { useStrategyData } from '@/lib/api/hooks';
import { useLanguageStore } from '@/lib/stores/language-store';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

interface PipelineSummaryResponse {
  labels: Record<string, string>;
}

export function HomeHeroCard() {
  const lang = useLanguageStore((s) => s.lang) ?? undefined;
  const params = lang ? { lang } : {};
  const { data: resp } = useStrategyData<PipelineSummaryResponse>('pipeline_summary', params);
  const L: Record<string, string> = (resp?.success ? resp.data?.labels : undefined) ?? {};

  // Time of day off the viewer's local clock — the server ships all
  // three phrasings (it can't know the viewer's timezone), this picks.
  // Evening wraps midnight: [0,5) and [18,24).
  const hour = new Date().getHours();
  const period =
    hour < 5 ? 'evening' : hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

  return (
    <div style={{ fontSize: surfaceBase.spacious }} className="text-center py-8">
      <h1 className={cn(text.display, 'mb-1')}>
        {L[`hero_title_${period}`]}
      </h1>
      <p className={text.lead}>
        {L.hero_subtitle}
      </p>
    </div>
  );
}
